import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Input,
  Row,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LogoutOutlined,
  QrcodeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { getHeaders } from '../../utility/getHeader';

const { Title, Text } = Typography;

const STATUS_TAG = {
  verified:  { color: 'success',  icon: <CheckCircleOutlined />,     label: 'VERIFIED'   },
  duplicate: { color: 'warning',  icon: <ExclamationCircleOutlined />, label: 'DUPLICATE' },
  not_found: { color: 'error',    icon: <CloseCircleOutlined />,      label: 'NOT FOUND' },
  error:     { color: 'error',    icon: <CloseCircleOutlined />,      label: 'ERROR'      },
};

const RESULT_BG = {
  verified:  '#f0fdf4',
  duplicate: '#fffbeb',
  not_found: '#fff1f0',
  error:     '#fff1f0',
};

const RESULT_BORDER = {
  verified:  '#52c41a',
  duplicate: '#faad14',
  not_found: '#ff4d4f',
  error:     '#ff4d4f',
};

export default function GateTicketScanner() {
  const navigate = useNavigate();
  const [barcodeInput, setBarcodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const inputRef = useRef(null);

  const uname    = localStorage.getItem('uname')    || sessionStorage.getItem('uname')    || 'Gate User';
  const usertype = localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '';

  const canScan = useMemo(() => barcodeInput.trim().length > 0 && !loading, [barcodeInput, loading]);

  const fetchRecentScans = async () => {
    try {
      const response = await axios.get('/gate/scan-logs?limit=20', getHeaders());
      if (response.data?.success) setRecentScans(response.data.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchRecentScans(); }, []);
  useEffect(() => { inputRef.current?.focus(); }, [loading]);

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/login');
  };

  const submitScan = async () => {
    const normalized = barcodeInput.trim();
    if (!normalized) return;
    setLoading(true);
    try {
      const response = await axios.post('/gate/scan-verify', { barcode_value: normalized }, getHeaders());
      const payload = response.data || {};
      setScanResult(payload);
      if (payload.scan_result === 'verified')       toast.success('Ticket verified successfully');
      else if (payload.scan_result === 'duplicate') toast.warning('Duplicate scan: ticket already used');
      else                                           toast.info(payload.message || 'Scan completed');
    } catch (error) {
      const payload = error.response?.data || { success: false, scan_result: 'error', message: 'Failed to verify ticket' };
      setScanResult(payload);
      toast.error(payload.message || 'Error verifying ticket');
    } finally {
      setLoading(false);
      setBarcodeInput('');
      fetchRecentScans();
    }
  };

  const onKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); submitScan(); } };

  const details = scanResult?.data;
  const status  = scanResult?.scan_result;
  const tag     = STATUS_TAG[status];

  const descItems = details ? [
    { key: 'barcode',   label: 'Barcode',         children: <Text copyable>{details.barcode_value}</Text> },
    { key: 'ticket_id', label: 'Ticket ID',        children: details.ticket_id    || '-' },
    { key: 'bill',      label: 'Bill Number',      children: details.bill_number  || '-' },
    { key: 'mode',      label: 'Print Mode',       children: details.print_mode   || '-' },
    { key: 'date',      label: 'Setup Date',       children: details.setup_date   || '-' },
    { key: 'cashier',   label: 'Cashier',          children: details.cashier_name || details.cashier_user_name || details.cashier_uname || '-' },
    { key: 'payment',   label: 'Payment',          children: details.payment_method || '-' },
    { key: 'total',     label: 'Total Amount',     children: details.total_amount  || '-' },
    { key: 'customer',  label: 'Customer',         children: details.customer_name || '-' },
    { key: 'phone',     label: 'Phone',            children: details.customer_phone || '-' },
    { key: 'verifat',   label: 'Verified At',      children: details.verified_at   || '-' },
  ] : [];

  const itemColumns = [
    { title: 'Ticket ID',  dataIndex: 'ticket_id',     key: 'ticket_id',     render: v => v || '-' },
    { title: 'Item',       dataIndex: 'item_name',     key: 'item_name' },
    { title: 'Qty',        dataIndex: 'quantity',      key: 'quantity',      width: 60 },
    { title: 'Price',      dataIndex: 'total_price',   key: 'total_price',   width: 90 },
  ];

  const logColumns = [
    { title: 'Time',    dataIndex: 'scanned_at',   key: 'scanned_at',   render: v => <Text style={{ fontSize: 11 }}>{v}</Text>, width: 130 },
    { title: 'Barcode', dataIndex: 'barcode_value', key: 'barcode_value', ellipsis: true },
    { title: 'Bill',    dataIndex: 'bill_number',   key: 'bill_number',   render: v => v || '-', width: 90 },
    {
      title: 'Result', dataIndex: 'scan_result', key: 'scan_result', width: 100,
      render: v => <Tag color={STATUS_TAG[v]?.color || 'default'}>{(STATUS_TAG[v]?.label || v || '').toUpperCase()}</Tag>,
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', display: 'flex', flexDirection: 'column' }}>
      {/* Mobile Header Bar */}
      <div style={{
        background: '#1d4ed8',
        color: '#fff',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <QrcodeOutlined style={{ fontSize: 22 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>Gate Scanner</div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>{uname} · {usertype}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to="/gate/ticket-details" style={{ color: '#bfdbfe', fontSize: 13, fontWeight: 600 }}>
            <SearchOutlined /> Details
          </Link>
          <Button
            type="text"
            size="small"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{ color: '#fca5a5' }}
          >
            Logout
          </Button>
        </div>
      </div>

      {/* Scrollable Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>

        {/* Scanner Input Card */}
        <Card
          bordered={false}
          style={{ borderRadius: 14, marginBottom: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}
          bodyStyle={{ padding: '16px 14px' }}
        >
          <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>
            <QrcodeOutlined style={{ marginRight: 6, color: '#1d4ed8' }} />
            Scan Ticket QR / Barcode
          </Text>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            Point scanner at QR code or type barcode and press Enter
          </Text>
          <Input.Search
            ref={inputRef}
            value={barcodeInput}
            onChange={(e) => setBarcodeInput(e.target.value)}
            onKeyDown={onKeyDown}
            onSearch={submitScan}
            placeholder="Scan or type barcode value"
            enterButton={
              <Button
                type="primary"
                loading={loading}
                disabled={!canScan}
                style={{ background: '#1d4ed8', borderColor: '#1d4ed8', minWidth: 100 }}
              >
                {loading ? 'Verifying…' : 'Verify'}
              </Button>
            }
            size="large"
            style={{ fontSize: 16 }}
            disabled={loading}
          />
        </Card>

        {/* Scan Result */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '30px 0' }}>
            <Spin size="large" tip="Verifying ticket…" />
          </div>
        )}

        {!loading && scanResult && (
          <Card
            bordered={false}
            style={{
              borderRadius: 14,
              marginBottom: 14,
              boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
              background: RESULT_BG[status] || '#fff',
              borderLeft: `5px solid ${RESULT_BORDER[status] || '#374151'}`,
            }}
            bodyStyle={{ padding: '14px 14px' }}
          >
            <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
              <Col>
                <Text strong style={{ fontSize: 15 }}>Scan Result</Text>
              </Col>
              <Col>
                {tag && (
                  <Tag color={tag.color} icon={tag.icon} style={{ fontSize: 13, padding: '4px 10px' }}>
                    {tag.label}
                  </Tag>
                )}
              </Col>
            </Row>
            <Text style={{ display: 'block', marginBottom: 10 }}>{scanResult.message}</Text>

            {details && (
              <>
                <Descriptions
                  items={descItems}
                  column={1}
                  size="small"
                  bordered
                  labelStyle={{ fontWeight: 600, background: '#f8fafc', width: '38%' }}
                  contentStyle={{ wordBreak: 'break-word' }}
                  style={{ marginBottom: 12 }}
                />

                {details.items?.length > 0 && (
                  <>
                    <Divider style={{ margin: '10px 0' }} />
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Ticket Items</Text>
                    <Table
                      columns={itemColumns}
                      dataSource={details.items.map((r, i) => ({ ...r, key: `${r.ticket_id || 'x'}-${i}` }))}
                      pagination={false}
                      size="small"
                      scroll={{ x: true }}
                    />
                  </>
                )}
              </>
            )}
          </Card>
        )}

        {/* Recent Scan Logs */}
        <Card
          title={<Text strong>Recent Scan Logs</Text>}
          bordered={false}
          style={{ borderRadius: 14, boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}
          bodyStyle={{ padding: '0 0 8px 0' }}
          extra={
            <Button size="small" onClick={fetchRecentScans} type="link" style={{ color: '#1d4ed8' }}>
              Refresh
            </Button>
          }
        >
          <Table
            columns={logColumns}
            dataSource={recentScans.map(r => ({ ...r, key: r.id }))}
            pagination={{ pageSize: 10, size: 'small' }}
            size="small"
            scroll={{ x: true }}
          />
        </Card>
      </div>
    </div>
  );
}
